/** Emails page — v1 dialer-comms.js emailsView/mailInit/mailRenderShell/
 * mailLoadThreads/mailOpenMsg, verbatim flow: account picker (admins see
 * every teammate's inbox), Refresh, 30-message list (unread highlight,
 * To:/From: naming, trimmed dates), reader pane with reply box. Reply +
 * Compose are LIVE (2026-07-23 go-live, item 2) through the same
 * /api/mail/send the lead threads already use — honest send states, threaded
 * replies (v1 mailSendReply). Connect Gmail stays in v1 (OAuth server flow). */
import { useCallback, useEffect, useRef, useState } from "react";
import { usePhone } from "../../lib/usePhone";
import { liveOn } from "../../lib/liveFire";
import { newIdemKey } from "../../lib/idem";
import {
  mailListDate, mailWho, reSubject, replyToOf,
  type MailAccount, type MailMsg, type MailSendPayload,
} from "./api";

export interface MailboxLoaders {
  accounts: () => Promise<MailAccount[]>;
  inbox: (accountId: string) => Promise<{ messages: MailMsg[]; reconnect: boolean; detail: string | null }>;
  message: (accountId: string, id: string) => Promise<{ subject: string; from: string; to: string; date: string; body: string; threadId: string | null } | null>;
  /** live-fire: resolves only on server-confirmed send, else throws */
  send: (p: MailSendPayload) => Promise<void>;
}

const PHASE_B = "Sending goes live in the sit-down session (Phase B)";
const CONNECT_V1 = "Connecting a Gmail account stays in v1 for now (Google OAuth runs on its server)";

export function MailboxPage({ loaders, isAdmin }: { loaders: MailboxLoaders; isAdmin: boolean }) {
  const [accounts, setAccounts] = useState<MailAccount[] | null>(null); // null = loading
  const [accId, setAccId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<MailMsg[]>([]);
  const [listNote, setListNote] = useState<string | null>(null);
  const [reconnect, setReconnect] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [reader, setReader] = useState<{ subject: string; from: string; to: string; date: string; body: string; threadId: string | null } | null>(null);
  const [readerBusy, setReaderBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const gen = useRef(0);
  const canSend = liveOn("emailSend");
  // reply box (v1 mailSendReply): honest lifecycle — typing → sending → sent ✓
  const [reply, setReply] = useState("");
  const [replyState, setReplyState] = useState<"idle" | "sending" | "sent">("idle");
  const [replyErr, setReplyErr] = useState<string | null>(null);
  const [composeOn, setComposeOn] = useState(false);
  const replyIdemRef = useRef<string | null>(null); // per-draft send key
  /** Phone = drill-in. "clicking on an email doesnt open it" (Tom,
   *  2026-07-30) — it DID open, into a reader pane stacked BELOW the whole
   *  list, a screen and a half offscreen. One pane at a time instead. */
  const isPhone = usePhone();

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !accId || !reader || replyState !== "idle") return;
    setReplyState("sending");
    setReplyErr(null);
    if (!replyIdemRef.current) replyIdemRef.current = newIdemKey();
    try {
      await loaders.send({
        accountId: accId,
        to: replyToOf(reader.from),
        subject: reSubject(reader.subject),
        body,
        threadId: reader.threadId,
        idemKey: replyIdemRef.current,
      });
      replyIdemRef.current = null; // confirmed — next reply gets a fresh key
      setReplyState("sent");
      setReply("");
      setTimeout(() => setReplyState("idle"), 2500);
      void loadInbox(accId); // v1: refresh the list so the reply shows
    } catch (e) {
      setReplyState("idle");
      setReplyErr((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Send failed");
    }
  };

  const loadInbox = useCallback(
    async (id: string) => {
      const g = ++gen.current;
      setBusy(true);
      setListNote(null);
      setReconnect(false);
      const r = await loaders.inbox(id);
      if (gen.current !== g) return;
      setBusy(false);
      if (r.reconnect) {
        setReconnect(true);
        setMsgs([]);
        return;
      }
      if (r.detail) {
        setListNote(r.detail);
        setMsgs([]);
        return;
      }
      setMsgs(r.messages);
    },
    [loaders],
  );

  useEffect(() => {
    let alive = true;
    void loaders.accounts().then((accs) => {
      if (!alive) return;
      setAccounts(accs);
      const first = accs[0];
      if (first) {
        setAccId(first.id);
        void loadInbox(first.id);
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMsg = async (id: string) => {
    if (!accId) return;
    setOpenId(id);
    setReaderBusy(true);
    setReader(null);
    const m = await loaders.message(accId, id);
    setReaderBusy(false);
    setReader(m);
  };

  return (
    <div className="mbx">
      <header className="pagehead">
        <div>
          <h1>Emails</h1>
          <span className="count">
            Send &amp; receive from your own mailbox.{isAdmin ? " As an admin you can view any teammate's inbox." : ""}
          </span>
        </div>
        <div className="mbxacts">
          <button className="btn" disabled title={CONNECT_V1}>📧 Connect Gmail</button>
          {canSend ? (
            <button className="btn primary" disabled={!accId} title={accId ? "New email from this mailbox" : "Connect a Gmail first"} onClick={() => setComposeOn(true)}>✏️ Compose</button>
          ) : (
            <button className="btn primary" disabled title={PHASE_B}>✏️ Compose</button>
          )}
        </div>
      </header>

      {accounts === null ? (
        <div className="skelstack" aria-hidden>
          <div className="skel" style={{ height: 48 }} />
          <div className="skel" style={{ height: 48 }} />
          <div className="skel" style={{ height: 48, opacity: 0.6 }} />
        </div>
      ) : accounts.length === 0 ? (
        <div className="mbxempty">
          <div className="mbxemptyico">✉️</div>
          <b>No mailbox connected yet</b>
          <span>
            Gmail gets connected in the live-fire session (Phase B) — until then, connect from v1's Emails page. You'll
            send and receive as yourself.
          </span>
        </div>
      ) : (
        <>
          <div className="mbxbar">
            {accounts.length > 1 || isAdmin ? (
              <select
                value={accId ?? ""}
                onChange={(e) => {
                  setAccId(e.target.value);
                  setOpenId(null);
                  setReader(null);
                  void loadInbox(e.target.value);
                }}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.email}</option>
                ))}
              </select>
            ) : (
              <span className="sub">{accounts[0]?.email}</span>
            )}
            <button className="btn" onClick={() => accId && void loadInbox(accId)} disabled={busy}>
              {busy ? "Loading…" : "⟳ Refresh"}
            </button>
          </div>

          <div className="mbxgrid">
            {/* phone: reading hides the list; ‹ back returns to it */}
            {isPhone && (openId || readerBusy) ? null : (
            <div className="mbxlist">
              {reconnect ? (
                <div className="mbxnote amber">
                  Google disconnected this Gmail (tokens expire periodically). Reconnect from v1's Emails page — the
                  Connect flow moves here in Phase B.
                </div>
              ) : listNote ? (
                <div className="mbxnote red">{listNote}</div>
              ) : busy && !msgs.length ? (
                <div className="skelstack" aria-hidden>
                  <div className="skel" style={{ height: 58 }} />
                  <div className="skel" style={{ height: 58 }} />
                  <div className="skel" style={{ height: 58 }} />
                  <div className="skel" style={{ height: 58, opacity: 0.6 }} />
                </div>
              ) : !msgs.length ? (
                <div className="mbxnote">No messages.</div>
              ) : (
                msgs.map((m) => (
                  <button key={m.id} className={"mbxrow" + (m.unread ? " unread" : "") + (openId === m.id ? " open" : "")} onClick={() => void openMsg(m.id)}>
                    <span className="mbxrowtop">
                      <span className="mbxwho">{m.direction === "out" ? "To: " + mailWho(m.to) : mailWho(m.from)}</span>
                      <span className="mbxdate">{mailListDate(m.date)}</span>
                    </span>
                    <span className="mbxsubj">{m.subject || "(no subject)"}</span>
                    <span className="mbxsnip">{m.snippet}</span>
                  </button>
                ))
              )}
            </div>
            )}

            {isPhone && !openId && !readerBusy ? null : (
            <div className="mbxread">
              {isPhone ? (
                <button className="dback" aria-label="Back to the email list" onClick={() => { setOpenId(null); setReader(null); setReaderBusy(false); }}>‹ Emails</button>
              ) : null}
              {readerBusy ? (
                <div className="mbxnote">Loading…</div>
              ) : reader ? (
                <>
                  <div className="mbxreadhead">
                    <h3>{reader.subject || "(no subject)"}</h3>
                    <div className="sub">From: {reader.from}</div>
                    <div className="sub">To: {reader.to}</div>
                    <div className="mbxdate">{reader.date}</div>
                  </div>
                  <div className="mbxbody">{reader.body}</div>
                  <div className="mbxreply">
                    <div className="sub">Reply to {replyToOf(reader.from)} · subject "{reSubject(reader.subject)}"</div>
                    <textarea rows={4} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} disabled={replyState === "sending"} />
                    {replyErr ? <div className="mbxnote red">{replyErr}</div> : null}
                    <div className="mbxreplyacts">
                      {canSend ? (
                        <button className="btn primary" disabled={!reply.trim() || replyState !== "idle"} onClick={() => void sendReply()}>
                          {replyState === "sent" ? "Sent ✓" : replyState === "sending" ? "Sending…" : "Send reply"}
                        </button>
                      ) : (
                        <button className="btn primary" disabled title={PHASE_B}>Send reply — Phase B</button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="mbxnote center">Select a message to read</div>
              )}
            </div>
            )}
          </div>
        </>
      )}

      {composeOn && accId ? (
        <ComposeModal
          fromEmail={accounts?.find((a) => a.id === accId)?.email ?? ""}
          onClose={() => setComposeOn(false)}
          onSend={async (to, subject, body, idemKey) => {
            await loaders.send({ accountId: accId, to, subject, body, idemKey });
            void loadInbox(accId);
          }}
        />
      ) : null}
    </div>
  );
}

/** v1 mailCompose modal — To / Subject / Body from the picked mailbox, with
 * the same honest send lifecycle as the lead-thread composer. */
function ComposeModal({ fromEmail, onClose, onSend }: {
  fromEmail: string;
  onClose: () => void;
  onSend: (to: string, subject: string, body: string, idemKey: string) => Promise<void>;
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [err, setErr] = useState<string | null>(null);
  const idemRef = useRef<string | null>(null); // per-draft send key
  const send = async () => {
    if (!to.trim() || state !== "idle") return;
    setState("sending");
    setErr(null);
    if (!idemRef.current) idemRef.current = newIdemKey();
    try {
      await onSend(to.trim(), subject.trim(), body, idemRef.current);
      setState("sent");
      setTimeout(onClose, 900);
    } catch (e) {
      setState("idle");
      setErr((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Send failed");
    }
  };
  return (
    <div className="cmpmodalscrim" onClick={state === "sending" ? undefined : onClose}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead"><h3>New email</h3><button className="close" onClick={onClose} disabled={state === "sending"}>×</button></div>
          <div className="mailform">
            <span className="sub">From: {fromEmail || "your connected mailbox"}</span>
            <label className="lbl">To</label>
            <input value={to} placeholder="name@email.com" onChange={(e) => setTo(e.target.value)} autoFocus />
            <label className="lbl">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
            <label className="lbl">Message</label>
            <textarea rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          {err ? <div className="mbxnote red">{err}</div> : null}
          <div className="expformacts">
            <button className="btn slim primary" disabled={!to.trim() || state !== "idle"} onClick={() => void send()}>
              {state === "sent" ? "Sent ✓" : state === "sending" ? "Sending…" : "Send"}
            </button>
            <button className="btn slim" onClick={onClose} disabled={state === "sending"}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
